import i18n from 'i18next';

import { FormTheme, isHexColour } from '@app/constants/theme';
import { FieldTypes, StandardFormFieldDto } from '@app/models/dtos/form';
import { PageJump } from '@app/models/types/form-builder-shared';
import { Ending, isJumpEndingMissing, withEndingIds } from '@app/utils/endings';
import { legacyTitleText } from '@app/utils/legacy-title';
import { extractTextfromJSON } from '@app/utils/richTextEditorExtenstion/get-html-from-json';
import { ContrastIssueKey, contrastKey, themeContrastGuard } from '@app/utils/theme-contrast';

/**
 * Honest defaults a form must meet before it is published. The backend
 * enforces the same rules on every publish (services/publish_checks.py —
 * keep the two in step); the builder checks first so the creator sees what
 * to fix, and where, before anything is sent.
 */

export type PublishProblemCode = 'missing_why_we_ask' | 'required_unanswerable' | 'preset_answer' | 'untitled_question' | 'missing_ending' | 'theme_contrast' | 'needs_review' | 'workspace_title_missing';

export interface PublishProblem {
    code: PublishProblemCode;
    /** The question; for `missing_ending` the page whose jump it is; for `theme_contrast` THEME_PROBLEM_ID; empty for `needs_review` and `workspace_title_missing`, which belong to no question. */
    fieldId: string;
    message: string;
    /** `needs_review` only: the id of the import report to look through (nothing of its content). */
    importId?: string | null;
    /** Advice, not a block: the problem is listed as a recommendation and the form can still be published (`theme_contrast` of an older form that is live with these colours). */
    advisory?: boolean;
    /** An `advisory` `theme_contrast` only: the failing pairings (the dialog words them itself, translated). */
    contrastIssues?: ContrastIssueKey[];
}

/** Most characters a "why we ask this" line may have (backend WHY_WE_ASK_MAX_LENGTH). */
export const WHY_WE_ASK_MAX_LENGTH = 280;

const IDENTIFYING_TYPES: ReadonlyArray<string> = [FieldTypes.EMAIL, FieldTypes.PHONE_NUMBER];
const ID_NUMBER_CANDIDATE_TYPES: ReadonlyArray<string> = [FieldTypes.SHORT_TEXT, FieldTypes.LONG_TEXT, FieldTypes.NUMBER];
// Kept narrow on purpose: whole words, and "ID"/"passport" only with "number".
const ID_NUMBER_TITLE =
    /\b(bsn|burgerservicenummer|sofinummer|sofi-nummer|passport\s*(?:number|no\.?|nr\.?)|paspoortnummer|paspoort\s*(?:nummer|nr\.?)|id[\s-]?(?:number|nummer|nr\.?|no\.?)|identification\s+number|identity\s+(?:card\s+)?number|identiteitsnummer|identiteitskaartnummer|national\s+(?:id|identity|insurance)\s+number|social\s+security\s+number|ssn)(?![\w-])/i;

const DISPLAY_ONLY_TYPES: ReadonlyArray<string> = [FieldTypes.TEXT, FieldTypes.STATEMENT, FieldTypes.IMAGE_CONTENT, FieldTypes.VIDEO_CONTENT, 'h1', 'h2', 'h3', 'h4', 'h5', 'p', 'strong', 'divider', 'markdown'];
const CHOICE_TYPES: ReadonlyArray<string> = [FieldTypes.MULTIPLE_CHOICE, FieldTypes.DROP_DOWN, FieldTypes.RANKING];
const PRESET_ANSWER_TYPES: ReadonlyArray<string> = [...CHOICE_TYPES, FieldTypes.YES_NO];

export function questionText(field: StandardFormFieldDto): string {
    const title = field?.title;
    const text = typeof title === 'string' ? title : title ? extractTextfromJSON({ ...field, title } as StandardFormFieldDto) : '';
    return (text || '').split(/\s+/).filter(Boolean).join(' ');
}

/**
 * The words of a field's own title, or '' when it has none: no per-type
 * placeholder stands in (an untitled question must be named before
 * publishing). A legacy plain title (`value`) counts (utils/legacy-title.ts).
 */
export function ownTitleText(field: StandardFormFieldDto): string {
    const title = field?.title;
    const walk = (node: any): string => {
        if (!node) return '';
        if (node.type === 'text') return node.text ?? '';
        if (node.type === 'answerPipe') return node.attrs?.label || node.attrs?.pipeKey || '';
        return (node.content ?? []).map(walk).join(' ');
    };
    let text = typeof title === 'string' ? title : title ? walk(title) : '';
    if (!title) text = legacyTitleText(field) ?? '';
    return text.split(/\s+/).filter(Boolean).join(' ');
}

/** Does this question collect an email, a phone number or an ID number? */
export function asksForIdentity(field: StandardFormFieldDto): boolean {
    if (IDENTIFYING_TYPES.includes(field?.type)) return true;
    if (ID_NUMBER_CANDIDATE_TYPES.includes(field?.type)) return ID_NUMBER_TITLE.test(questionText(field));
    return false;
}

function label(field: StandardFormFieldDto): string {
    let text = questionText(field);
    if (text.length > 60) text = text.slice(0, 57).trimEnd() + '...';
    return text ? `"${text}"` : i18n.t('builder:PUBLISH_PROBLEM.A_QUESTION');
}

const choiceText = (choice: any) => String(choice?.value || choice?.label || '').trim();

/**
 * Does the field's `value` pick one of its own options? Older form formats
 * used `value` as a default answer; today it only holds a legacy title,
 * which names no option.
 */
export function hasPresetAnswer(field: StandardFormFieldDto): boolean {
    if (!PRESET_ANSWER_TYPES.includes(field?.type)) return false;
    const value = String(field?.value ?? '')
        .trim()
        .toLowerCase();
    if (!value) return false;
    const options = (field?.properties?.choices ?? []).map((choice) => choiceText(choice).toLowerCase());
    if (field.type === FieldTypes.YES_NO) options.push('yes', 'no');
    return options.includes(value);
}

function unanswerableReason(field: StandardFormFieldDto): string | null {
    const type = field?.type;
    if (DISPLAY_ONLY_TYPES.includes(type)) return i18n.t('builder:PUBLISH_PROBLEM.REASON_DISPLAY_ONLY');
    if (type === FieldTypes.GROUP && !field?.properties?.repeat) return i18n.t('builder:PUBLISH_PROBLEM.REASON_GROUP_HEADING');
    if (CHOICE_TYPES.includes(type) && !(field?.properties?.choices ?? []).some((choice) => choiceText(choice))) return i18n.t('builder:PUBLISH_PROBLEM.REASON_NO_OPTIONS');
    if (type === FieldTypes.MATRIX) {
        const rows = field?.properties?.fields ?? [];
        if (rows.length && !rows.some((row) => (row?.properties?.choices ?? []).some((choice) => choiceText(choice)))) return i18n.t('builder:PUBLISH_PROBLEM.REASON_NO_COLUMNS');
    }
    return null;
}

/** Every question of the form: page fields and the questions inside groups, with where they are. */
function questions(slides: Array<StandardFormFieldDto> | undefined): Array<{ field: StandardFormFieldDto; where: string }> {
    const out: Array<{ field: StandardFormFieldDto; where: string }> = [];
    (slides ?? []).forEach((slide, pageIndex) => {
        const fields = slide?.type === FieldTypes.SLIDE ? (slide?.properties?.fields ?? []) : [slide];
        fields.forEach((field, fieldIndex) => {
            out.push({ field, where: i18n.t('builder:PUBLISH_PROBLEM.WHERE_QUESTION', { n: fieldIndex + 1, page: pageIndex + 1 }) });
            if (field?.type === FieldTypes.GROUP) {
                (field?.properties?.fields ?? []).forEach((child, childIndex) => out.push({ field: child, where: i18n.t('builder:PUBLISH_PROBLEM.WHERE_GROUP_QUESTION', { n: childIndex + 1, page: pageIndex + 1 }) }));
            }
        });
    });
    return out;
}

/**
 * Everything that keeps this form from being published, in form order.
 * `endings`: the form's endings; when given, a page jump to an ending the
 * form no longer has is a problem too.
 */
export function getPublishProblems(slides: Array<StandardFormFieldDto> | undefined, endings?: Ending[] | null): PublishProblem[] {
    const problems: PublishProblem[] = [];
    questions(slides).forEach(({ field, where }) => {
        if (!field || field.internal) return;
        if (!DISPLAY_ONLY_TYPES.includes(field.type) && !ownTitleText(field)) {
            problems.push({ code: 'untitled_question', fieldId: field.id, message: i18n.t('builder:PUBLISH_PROBLEM.untitled_question', { where }) });
        }
        const name = label(field);
        if (hasPresetAnswer(field)) {
            problems.push({ code: 'preset_answer', fieldId: field.id, message: i18n.t('builder:PUBLISH_PROBLEM.preset_answer', { name }) });
        }
        if (field.validations?.required) {
            const reason = unanswerableReason(field);
            if (reason) problems.push({ code: 'required_unanswerable', fieldId: field.id, message: i18n.t('builder:PUBLISH_PROBLEM.required_unanswerable', { name, reason }) });
        }
        if (asksForIdentity(field) && !field.properties?.whyWeAsk?.trim()) {
            problems.push({ code: 'missing_why_we_ask', fieldId: field.id, message: i18n.t('builder:PUBLISH_PROBLEM.missing_why_we_ask', { name }) });
        }
    });
    if (endings !== undefined) {
        const known = withEndingIds(endings);
        (slides ?? []).forEach((slide, index) => {
            ((slide?.properties?.jumps as PageJump[] | undefined) ?? []).forEach((jump) => {
                if (isJumpEndingMissing(jump, known)) problems.push({ code: 'missing_ending', fieldId: slide.id, message: i18n.t('builder:PUBLISH_PROBLEM.missing_ending', { page: index + 1 }) });
            });
        });
    }
    return problems;
}

/** The `fieldId` of the theme's problem: it belongs to no question. */
export const THEME_PROBLEM_ID = 'theme';

const CONTRAST_WORDS: Record<ContrastIssueKey, string> = {
    text: 'questions and text on the page',
    answer: 'answers in the input boxes',
    button: 'white text on buttons',
    outlined: 'text and edges of outlined buttons',
    border: 'input borders',
    action: 'chosen answers and progress'
};

/** The first publish-checks version that always holds a form's theme to the contrast rule (backend `THEME_CONTRAST_FROM_VERSION`). */
export const THEME_CONTRAST_FROM_VERSION = 3;

/** Is a form created under `publishChecksVersion` always held to the contrast rule? Older forms are held to it only when their colours changed (backend `requires_theme_contrast`). */
export function requiresThemeContrast(publishChecksVersion?: number | null): boolean {
    return !!publishChecksVersion && publishChecksVersion >= THEME_CONTRAST_FROM_VERSION;
}

/**
 * The colours of a form's live version, as the builder knows them: the theme
 * (the live version's colours), `null` (never published), or `undefined` (not
 * known: the server decides).
 */
export type LiveTheme = Partial<FormTheme> | null | undefined;

/**
 * What a failing theme does at publish (backend `theme_contrast_blocks`):
 * - `block`: a new form (`requiresThemeContrast`), a form that was never
 *   published (nothing live relies on its colours, and nothing shows they are
 *   unchanged), or a form whose colours differ from its live version's (what
 *   the check reads: `contrastKey`);
 * - `advisory`: an older form republished with the colours it went live with;
 * - `server`: an older form whose live colours are not known here; nothing is
 *   claimed and the server, which reads them, decides.
 */
export type ThemeContrastGate = 'block' | 'advisory' | 'server';

export function themeContrastGate(publishChecksVersion: number | null | undefined, theme: Partial<FormTheme> | null | undefined, live: LiveTheme): ThemeContrastGate {
    if (requiresThemeContrast(publishChecksVersion) || live === null) return 'block';
    if (live === undefined) return 'server';
    return contrastKey(theme) !== contrastKey(live) ? 'block' : 'advisory';
}

/** Does the theme have the four colours the contrast rule reads? One without them (an old form on the defaults) isn't checked. */
export const hasGuardedColours = (theme: Partial<FormTheme> | null | undefined): theme is Partial<FormTheme> => !!theme && (['primary', 'secondary', 'tertiary', 'accent'] as const).every((role) => isHexColour(theme[role]));

/**
 * The form's theme, when its own colours fail WCAG AA contrast where the
 * respondent form shows them (text 4.5:1, controls 3:1; utils/theme-contrast.ts
 * themeContrastGuard; an outlined button is measured by its real text and edge
 * colours). Every preset passes; a custom or saved palette may not. It stops
 * publishing as `gate` says (`themeContrastGate`; `block` unless told): for
 * `advisory` the problem is only advice, for `server` nothing is claimed. A
 * theme without its four colours (an old form on the defaults) isn't checked.
 */
export function themeContrastProblems(theme: Partial<FormTheme> | null | undefined, gate: ThemeContrastGate = 'block'): PublishProblem[] {
    if (gate === 'server' || !hasGuardedColours(theme)) return [];
    const { issues } = themeContrastGuard(theme as FormTheme);
    if (!issues.length) return [];
    const words = issues.map((issue) => i18n.t(`builder:PUBLISH_CONTRAST.WORD_${issue.key}`, { defaultValue: CONTRAST_WORDS[issue.key] }));
    const list = words.length > 1 ? `${words.slice(0, -1).join(', ')}${i18n.t('builder:PUBLISH_CONTRAST.AND')}${words[words.length - 1]}` : words[0];
    const advisory = gate === 'advisory';
    const message = advisory
        ? `The theme's colours are hard to read: ${list}. This form was made before the colour check, so it can still be published as it is; colours you change later will need to pass.`
        : i18n.t('builder:PUBLISH_CONTRAST.BLOCK', { pairs: list });
    return [{ code: 'theme_contrast', fieldId: THEME_PROBLEM_ID, message, ...(advisory ? { advisory: true, contrastIssues: issues.map((issue) => issue.key) } : {}) }];
}

// Characters that show nothing: format, space, line/paragraph separator and control characters (zero-width space,
// word joiner, BOM, NBSP, ...), plus the fillers and blanks Unicode files elsewhere (Hangul and Braille blanks, soft
// hyphen, Mongolian vowel separator). Same set as the backend's `visible_title` (services/publish_checks.py).
const INVISIBLE = new RegExp('[\\p{Cf}\\p{Zs}\\p{Zl}\\p{Zp}\\p{Cc}\\u115f\\u1160\\u3164\\uffa0\\u2800\\u00ad\\u180e]', 'gu');

/** The characters of a title that can be seen; '' when there are none. */
export function visibleTitle(title: string | null | undefined): string {
    return typeof title === 'string' ? title.replace(INVISIBLE, '') : '';
}

/**
 * Does the workspace have a title respondents can read? It needs a visible character, and "untitled" (any case) is
 * the placeholder, not a title: the onboarding page and the login redirect treat it as no title too.
 */
export function hasWorkspaceTitle(title: string | null | undefined): boolean {
    const visible = visibleTitle(title);
    return visible !== '' && visible.toLowerCase() !== 'untitled';
}

/** The first publish-checks version that holds a form to the workspace-title rule (backend `WORKSPACE_TITLE_FROM_VERSION`). */
export const WORKSPACE_TITLE_FROM_VERSION = 2;

/** Is a form created under `publishChecksVersion` held to the workspace-title rule? Forms made before it (or with no version) are not (backend `requires_workspace_title`). */
export function requiresWorkspaceTitle(publishChecksVersion?: number | null): boolean {
    return !!publishChecksVersion && publishChecksVersion >= WORKSPACE_TITLE_FROM_VERSION;
}

/**
 * The workspace's title is who respondents see receiving their answers, so a
 * form created under the workspace-title rule is not published while the
 * workspace has none (backend `workspace_title_problem`, the same rule:
 * `hasWorkspaceTitle`). An older form is not held to it. The handle never
 * stands in for a title. While the workspace is still loading (no id)
 * nothing is claimed here: the server decides.
 */
export function workspaceTitleProblems(workspace: { id?: string | null; title?: string | null } | null | undefined, publishChecksVersion?: number | null): PublishProblem[] {
    if (!requiresWorkspaceTitle(publishChecksVersion)) return [];
    if (!workspace?.id || hasWorkspaceTitle(workspace.title)) return [];
    return [{ code: 'workspace_title_missing', fieldId: '', message: i18n.t('builder:PUBLISH_PROBLEM.workspace_title_missing') }];
}

/** The problems a refused publish (422 form_not_publishable) carries, if any. */
export function problemsFromPublishError(error: any): PublishProblem[] {
    const body = error?.data ?? error;
    if (body?.code !== 'form_not_publishable' || !Array.isArray(body?.problems)) return [];
    return body.problems.filter((problem: any) => typeof problem?.message === 'string');
}

/** The "this import still needs review" problem among a refusal's problems, if any. */
export function needsReviewProblem(problems: PublishProblem[]): PublishProblem | undefined {
    return problems.find((problem) => problem.code === 'needs_review');
}

/**
 * The problem a refused template carries (422 `needs_review`: the form is an
 * import that still needs review), if the error is that refusal. It has the
 * problem shape of a refused publish, with the same `importId`.
 */
export function templateReviewProblem(error: any): PublishProblem | undefined {
    const body = error?.data ?? error;
    if (body?.code !== 'needs_review' || !Array.isArray(body?.problems)) return undefined;
    return needsReviewProblem(body.problems.filter((problem: any) => typeof problem?.message === 'string'));
}

/**
 * Which problems stop publishing and which are only recommended. "Why we ask
 * this" is required of forms created under the publish checks
 * (`settings.publishChecksVersion`); forms made before them were published
 * without it and stay publishable, so for them it is a recommendation, and so
 * is an `advisory` problem (the colours of an older form that is live with
 * them). The other rules hold for every form (backend `ensure_publishable`).
 */
export function splitPublishProblems(problems: PublishProblem[], publishChecksVersion?: number | null): { blocking: PublishProblem[]; recommended: PublishProblem[] } {
    const requiresReason = !!publishChecksVersion && publishChecksVersion >= 1;
    const recommended = problems.filter((problem) => problem.advisory || (!requiresReason && problem.code === 'missing_why_we_ask'));
    return { blocking: problems.filter((problem) => !recommended.includes(problem)), recommended };
}
